import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import TalentTag from '../TalentTag/TalentTag';
import TooltipAction from '../TooltipAction/TooltipAction';
import iconEditThinRed from '../../assets/icon-edit-thin-red.svg';
import TagColorPicker from '../TagColorPicker/TagColorPicker';
import tagsService from '../../services/tagsService';
import './TalentTagsEditor.css';

const TalentTagsEditor = ({
  talentId,
  talentTags = [],
  onTagsUpdate,
  expanded = true,
  onToggleExpand,
  onEditRequest
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [companyTags, setCompanyTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tagColors, setTagColors] = useState([]);
  const [editingTag, setEditingTag] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', colorHex: '' });
  const [localTags, setLocalTags] = useState(talentTags || []);
  const [tooltip, setTooltip] = useState({ visible: false, message: '', position: null });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    setLocalTags(talentTags || []);
  }, [talentTags]);

  useEffect(() => {
    if (showDropdown) {
      loadCompanyTags();
      loadTagColors();
    }
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Listen for global tag updates to keep dropdown list fresh
  useEffect(() => {
    const handleGlobalTagUpdate = (e) => {
      const updated = e.detail;
      if (updated && updated.id) {
        setCompanyTags(prev => prev.map(tag => tag.id === updated.id ? { ...tag, ...updated } : tag));
        // Also update local tags if assigned
        setLocalTags(prev => prev.map(tag => tag.id === updated.id ? { ...tag, ...updated } : tag));
      }
    };
    window.addEventListener('tag-updated', handleGlobalTagUpdate);
    return () => window.removeEventListener('tag-updated', handleGlobalTagUpdate);
  }, []);

  const loadCompanyTags = async () => {
    try {
      setLoading(true);
      const response = await tagsService.getCompanyTags(false);
      setCompanyTags(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
      toast.error('Erro ao carregar tags');
    } finally {
      setLoading(false);
    }
  };

  const loadTagColors = async () => {
    try {
      const response = await tagsService.getTagColors();
      setTagColors(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar cores:', error);
    }
  };

  const handleAddTag = async (tag) => {
    try {
      await tagsService.addTagToTalent(talentId, tag.id);
      setLocalTags((prev) => {
        const newTags = [...prev, tag];
        if (onTagsUpdate) onTagsUpdate(newTags);
        return newTags;
      });
    } catch (error) {
      console.error('Erro ao adicionar tag:', error);
      const errorMessage = error.response?.data?.errors?.[0]?.title || 'Erro ao adicionar tag';
      toast.error(errorMessage);
    }
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setFormData({ name: tag.name || '', colorHex: tag.colorHex });
    setShowModal(true);
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Arquivar esta tag?')) return;
    try {
      await tagsService.archiveTag(tagId);
      setShowModal(false);
      setEditingTag(null);
      setFormData({ name: '', colorHex: '#E63946' });
      await loadCompanyTags();
      setLocalTags((prev) => {
        const newTags = prev.filter((t) => t.id !== tagId);
        if (onTagsUpdate) onTagsUpdate(newTags);
        return newTags;
      });
    } catch (error) {
      console.error('Erro ao arquivar tag:', error);
      toast.error('Erro ao arquivar tag');
    }
  };

  const handleRemoveTag = async (tagId) => {
    try {
      await tagsService.removeTagFromTalent(talentId, tagId);
      setLocalTags((prev) => {
        const newTags = prev.filter((t) => t.id !== tagId);
        if (onTagsUpdate) onTagsUpdate(newTags);
        return newTags;
      });
    } catch (error) {
      toast.error('Erro ao remover tag', error);
    }
  };

  const isTagAssigned = (tagId) => {
    return localTags.some((t) => t.id === tagId);
  };

  return (
    <div className="talentTagsEditor">
      <div className="tagsContainer">
        {localTags.map((tag) => (
          <TalentTag
            key={tag.id}
            text={tag.name}
            color={tag.colorHex}
            onRemove={() => handleRemoveTag(tag.id)}
            onClick={onToggleExpand || (() => setShowDropdown(true))}
            showText={expanded}
          />
        ))}

        <button
          ref={buttonRef}
          className={`addTagButton ${!expanded ? 'addTagButtonMinimized' : ''}`}
          onClick={() => {
            if (onEditRequest) {
              onEditRequest();
            } else {
              setShowDropdown(!showDropdown);
            }
          }}
          onMouseEnter={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setTooltip({
              visible: true,
              message: 'Use as etiquetas para organizar as informações dos talentos',
              position: {
                top: rect.top + rect.height / 2,
                left: rect.left + rect.width + 8,
              },
            });
          }}
          onMouseLeave={() => setTooltip({ visible: false, message: '', position: null })}
        >
          {expanded ? '+ Etiqueta' : '+'}
        </button>
      </div>

      {showDropdown && (
        <div ref={dropdownRef} className="tagsDropdown">
          <div className="dropdownHeader">
            <div className="dropdownHeaderTitle">Tags</div>
            <button
              className="closeButton"
              onClick={() => setShowDropdown(false)}
            >
              ×
            </button>
          </div>

          <div className="dropdownContent">
            {loading ? (
              <div className="loadingText">Carregando...</div>
            ) : (
              <>
                {companyTags.map((tag) => {
                  const assigned = isTagAssigned(tag.id);
                  return (
                    <div
                      key={tag.id}
                      className={`tagOption ${assigned ? 'tagOptionAssigned' : ''}`}
                    >
                      <label className="checkboxLabel">
                        <input
                          type="checkbox"
                          checked={assigned}
                          onChange={() => {
                            if (assigned) {
                              handleRemoveTag(tag.id);
                            } else {
                              handleAddTag(tag);
                            }
                          }}
                        />
                        <TalentTag
                          text={tag.name || ''}
                          color={tag.colorHex}
                          showText={true}
                          onClick={(e) => {
                            e.preventDefault();
                            if (assigned) {
                              handleRemoveTag(tag.id);
                            } else {
                              handleAddTag(tag);
                            }
                          }}
                        />
                      </label>
                      <button
                        className="editButton"
                        onClick={() => handleEditTag(tag)}
                      >
                        <img src={iconEditThinRed} alt="Editar" style={{ width: 12, height: 12 }} />
                      </button>
                    </div>
                  );
                })}
                {companyTags.length === 0 && (
                  <div className="emptyText">
                    Nenhuma tag disponível
                  </div>
                )}
              </>
            )}
          </div>

          <div
            className="createButton"
            onClick={() => {
              setEditingTag(null);
              setFormData({
                name: '',
                colorHex: tagColors?.[0]?.colorHex || '#EC5569',
              });
              setShowModal(true);
            }}
          >
            Criar Tag
          </div>
        </div>
      )}

      {showModal && (
        <div className="modalOverlay" onClick={() => setShowModal(false)}>
          <div
            className="modalContent"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modalHeader">
              <h3>{editingTag ? 'Editar Tag' : 'Nova Tag'}</h3>
              <button
                className="modalClose"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <div className="formGroup">
              <label>Nome da Tag (opcional)</label>
              <input
                type="text"
                value={formData.name}
                maxLength={50}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="formInput"
              />
            </div>

            <div className="formGroup">
              <label>Cor</label>
              <TagColorPicker
                selectedColor={formData.colorHex}
                onColorSelect={(color) => setFormData({ ...formData, colorHex: color })}
              />
            </div>

            <div className="formActions">
              {editingTag && (
                <button
                  className="btnDanger"
                  onClick={() => handleDeleteTag(editingTag.id)}
                >
                  Arquivar
                </button>
              )}
              <button
                className="btnSecondary"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </button>
              <button
                className="btnPrimary"
                onClick={async () => {
                  try {
                    let savedTag = null;

                    if (editingTag) {
                      await tagsService.updateCompanyTag(
                        editingTag.id,
                        formData.name ? formData.name.trim() : '',
                        formData.colorHex
                      );
                      savedTag = { ...editingTag, name: formData.name, colorHex: formData.colorHex };

                      // Update lists locally
                      setCompanyTags(prev => prev.map(t => t.id === savedTag.id ? savedTag : t));
                      setLocalTags(prev => prev.map(t => t.id === savedTag.id ? { ...t, ...savedTag } : t));

                      // Notify parent if needed (for simple refresh)
                      if (onTagsUpdate) onTagsUpdate(); // Or pass updated list if signature allows, but here we just updated localTags

                      // Dispatch global event
                      window.dispatchEvent(new CustomEvent('tag-updated', { detail: savedTag }));

                      // Auto-assign if not already assigned
                      if (!isTagAssigned(savedTag.id)) {
                        await handleAddTag(savedTag);
                      }

                    } else {
                      const response = await tagsService.createCompanyTag(
                        formData.name ? formData.name.trim() : '',
                        formData.colorHex
                      );
                      savedTag = response?.data;

                      if (savedTag) {
                        setCompanyTags(prev => [...prev, savedTag]);
                        // Auto-assign to current talent
                        await handleAddTag(savedTag);
                      } else {
                        await loadCompanyTags();
                      }
                    }

                    setShowModal(false);
                    setEditingTag(null);
                  } catch (error) {
                    console.error('Erro ao salvar tag:', error);
                    const msg = error.response?.data?.errors?.[0]?.title || 'Erro ao salvar tag';
                    toast.error(msg);
                  }
                }}
              >
                {editingTag ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tooltip.visible && tooltip.position && ReactDOM.createPortal(
        <TooltipAction
          message={tooltip.message}
          visible={tooltip.visible}
          style={{
            position: 'fixed',
            top: `${tooltip.position.top}px`,
            left: `${tooltip.position.left}px`,
            transform: 'translateY(-50%)',
            zIndex: 999999,
          }}
        />,
        document.body
      )}
    </div>
  );
};

export default TalentTagsEditor;
